import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { Select } from '../../components/ui'
import HistoricoContatosAgrupadosTable, {
  type ContatosPortalSortKey,
} from '../../components/helpdesk/HistoricoContatosAgrupadosTable'
import { groupLigacoesByCorretor } from '../../components/helpdesk/contatosPortalAgrupamento'
import { comercialCadastroService } from '../../services/comercialCadastro'
import { comercialContatosPortalService } from '../../services/comercialContatosPortal'
import type {
  ContactLogsRelatorioFiltros,
  ContatoCorretorGrupo,
  ContatosDetalheFiltros,
} from '../../types/comercialContatosPortal'
import {
  ContatosPortalCorretorFiltroInput,
  ContatosPortalFiltrosSection,
  ContatosPortalPeriodoFiltroInput,
  ContatosPortalResponsavelFiltroInput,
  FiltroField,
  contatosPortalFiltroFieldClass,
} from './ContatosPortalFiltros'
import { ContatosPortalTableSection } from './ContatosPortalTableSection'
import { useContatosPortalFiltros } from './useContatosPortalFiltros'

const PAGE_SIZE = 20

function meioContatoLabel(tipo: ContatoCorretorGrupo['resumo']['tipo_contato']): string {
  if (tipo === 'telefone') return 'Telefone'
  if (tipo === 'email') return 'E-Mail'
  if (tipo === 'whatsapp') return 'WhatsApp'
  return ''
}

function valorOrdenacao(grupo: ContatoCorretorGrupo, key: ContatosPortalSortKey): string {
  const resumo = grupo.resumo
  switch (key) {
    case 'comercial_responsavel':
      return (resumo.comercial_responsavel_nome || '').trim()
    case 'consultant_responsavel':
      return (resumo.consultant_responsavel_nome || '').trim()
    case 'corretor':
      return (grupo.corretora_nome || '').trim()
    case 'data_horario':
      return `${resumo.data || ''}T${resumo.horario || ''}`
    case 'meio':
      return meioContatoLabel(resumo.tipo_contato)
    case 'tipo':
      return resumo.tipo === 'ativo' ? 'Ativo' : resumo.tipo === 'receptivo' ? 'Receptivo' : ''
    case 'sucesso':
      return resumo.teve_contato ? 'Sim' : 'Não'
    case 'motivo':
      return (resumo.motivo || '').trim()
  }
}

const ContatosPortalHistoricoTab: React.FC = () => {
  const {
    isComercialFilterRestricted,
    loadingComercialUsers,
    loadingConsultantUsers,
    comerciaisOptions,
    consultoresOptions,
    brokerId,
    selectedBroker,
    setCorretor,
    setComercialUserId,
    consultantUserId,
    setConsultantUserId,
    periodo,
    setPeriodo,
    periodoInvalido,
    setPeriodoInvalido,
    displayedComercialUserId,
    effectiveComercialUserId,
    validatePeriodo,
    resetFiltrosBase,
  } = useContatosPortalFiltros()

  const [motivos, setMotivos] = useState<{ id: string; name: string }[]>([])
  const [motivoId, setMotivoId] = useState('')
  const [tipoContato, setTipoContato] = useState<'ativo' | 'receptivo' | ''>('')
  const [meioContato, setMeioContato] = useState<'telefone' | 'email' | 'whatsapp' | ''>('')
  const [sucessoContato, setSucessoContato] = useState<'com_sucesso' | 'sem_sucesso' | ''>('')

  const [grupos, setGrupos] = useState<ContatoCorretorGrupo[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [emptyMessage, setEmptyMessage] = useState('Nenhum registro encontrado.')
  const [hasSearched, setHasSearched] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [tableKey, setTableKey] = useState(0)
  const [sortKey, setSortKey] = useState<ContatosPortalSortKey>('data_horario')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc')

  const gruposOrdenados = useMemo(() => {
    return [...grupos].sort((a, b) => {
      const cmp = valorOrdenacao(a, sortKey).localeCompare(valorOrdenacao(b, sortKey), 'pt-BR', {
        sensitivity: 'base',
        numeric: true,
      })
      if (cmp !== 0) return sortOrder === 'asc' ? cmp : -cmp
      return a.corretor_id.localeCompare(b.corretor_id)
    })
  }, [grupos, sortKey, sortOrder])

  const total = gruposOrdenados.length
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / PAGE_SIZE)), [total])

  const gruposPagina = useMemo(() => {
    const from = (currentPage - 1) * PAGE_SIZE
    return gruposOrdenados.slice(from, from + PAGE_SIZE)
  }, [gruposOrdenados, currentPage])

  useEffect(() => {
    void (async () => {
      const motivosData = await comercialCadastroService.listContactReasons()
      setMotivos(motivosData)
    })()
  }, [])

  /** Escopo da busca no servidor: comercial, consultor, corretor e período. */
  const buildFiltrosEscopo = useCallback((): ContactLogsRelatorioFiltros => {
    const filtros: ContactLogsRelatorioFiltros = {}
    if (brokerId) filtros.broker_id = brokerId
    // Ignora sentinel `\0` (usuário restringido sem profile carregado).
    if (effectiveComercialUserId && effectiveComercialUserId !== '\0') {
      filtros.comercial_user_id = effectiveComercialUserId
    }
    if (consultantUserId) filtros.consultant_user_id = consultantUserId
    filtros.contact_date_inicio = periodo.dataInicio
    filtros.contact_date_fim = periodo.dataFim
    return filtros
  }, [brokerId, consultantUserId, effectiveComercialUserId, periodo])

  /**
   * Meio / tipo / sucesso / motivo: só definem quais corretoras entram na listagem
   * e o contato-resumo. O histórico expandido permanece com todos do período.
   */
  const detalheFiltros = useMemo((): ContatosDetalheFiltros => {
    const filtros: ContatosDetalheFiltros = {}
    if (motivoId) filtros.commercial_contact_reason_id = motivoId
    if (tipoContato) filtros.call_direction = tipoContato
    if (meioContato) filtros.contact_channel = meioContato
    if (sucessoContato === 'com_sucesso') filtros.contact_made = true
    if (sucessoContato === 'sem_sucesso') filtros.contact_made = false
    return filtros
  }, [meioContato, motivoId, sucessoContato, tipoContato])

  const carregar = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      if (effectiveComercialUserId === '\0') {
        setError('Não foi possível identificar o usuário logado para filtrar os contatos.')
        setGrupos([])
        setHasSearched(true)
        return
      }
      const periodoErro = validatePeriodo()
      if (periodoErro) {
        setPeriodoInvalido(true)
        setError(periodoErro)
        setGrupos([])
        setHasSearched(true)
        return
      }
      setPeriodoInvalido(false)
      const resultado = await comercialContatosPortalService.listContactLogsForRelatorio(
        buildFiltrosEscopo()
      )
      if (resultado.error) {
        setError(`Não foi possível carregar o histórico de contatos: ${resultado.error}`)
        setGrupos([])
        setHasSearched(true)
        return
      }
      setGrupos(groupLigacoesByCorretor(resultado.data, detalheFiltros))
      setCurrentPage(1)
      setTableKey(k => k + 1)
      setEmptyMessage(
        resultado.empty_reason === 'comercial_sem_corretoras'
          ? 'Nenhum corretor vinculado a este comercial responsável.'
          : resultado.empty_reason === 'consultant_sem_corretoras'
            ? 'Nenhum corretor vinculado a este consultor responsável.'
            : 'Nenhum registro encontrado.'
      )
      setHasSearched(true)
    } catch (e) {
      console.error('[ContatosPortalHistoricoTab]', e)
      setError('Não foi possível carregar o histórico de contatos.')
      setGrupos([])
      setHasSearched(true)
    } finally {
      setLoading(false)
    }
  }, [
    buildFiltrosEscopo,
    detalheFiltros,
    effectiveComercialUserId,
    setPeriodoInvalido,
    validatePeriodo,
  ])

  const handleBuscar = () => {
    void carregar()
  }

  const handleLimparFiltros = () => {
    resetFiltrosBase()
    setMotivoId('')
    setTipoContato('')
    setMeioContato('')
    setSucessoContato('')
    setError(null)
    setEmptyMessage('Nenhum registro encontrado.')
    setHasSearched(false)
    setGrupos([])
    setCurrentPage(1)
    setTableKey(k => k + 1)
  }

  const handlePageChange = (page: number) => {
    if (loading) return
    setCurrentPage(page)
    setTableKey(k => k + 1)
  }

  const handleColumnSort = (key: string) => {
    if (sortKey === key) {
      setSortOrder(order => (order === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortKey(key as ContatosPortalSortKey)
      setSortOrder('asc')
    }
    setCurrentPage(1)
    setTableKey(k => k + 1)
  }

  return (
    <div className="flex flex-col">
      <ContatosPortalFiltrosSection onBuscar={handleBuscar} onLimpar={handleLimparFiltros} loading={loading}>
        <FiltroField label="Comercial responsável">
          <ContatosPortalResponsavelFiltroInput
            value={displayedComercialUserId}
            onChange={setComercialUserId}
            usuarios={comerciaisOptions}
            disabled={loadingComercialUsers || isComercialFilterRestricted}
            restricted={isComercialFilterRestricted}
          />
        </FiltroField>
        <FiltroField label="Consultor responsável">
          <ContatosPortalResponsavelFiltroInput
            value={consultantUserId}
            onChange={setConsultantUserId}
            usuarios={consultoresOptions}
            disabled={loadingConsultantUsers}
          />
        </FiltroField>
        <FiltroField label="Corretor">
          <ContatosPortalCorretorFiltroInput
            value={brokerId}
            initialBroker={selectedBroker}
            onChange={setCorretor}
          />
        </FiltroField>
        <ContatosPortalPeriodoFiltroInput
          layout="grid"
          value={periodo}
          onChange={next => {
            setPeriodo(next)
            setPeriodoInvalido(false)
          }}
          invalid={periodoInvalido}
        />
        <FiltroField label="Meio de contato">
          <Select
            value={meioContato}
            onChange={e => setMeioContato(e.target.value as 'telefone' | 'email' | 'whatsapp' | '')}
            className={contatosPortalFiltroFieldClass}
          >
            <option value="">Todos</option>
            <option value="telefone">Telefone</option>
            <option value="email">E-Mail</option>
            <option value="whatsapp">WhatsApp</option>
          </Select>
        </FiltroField>
        <FiltroField label="Tipo de contato">
          <Select
            value={tipoContato}
            onChange={e => setTipoContato(e.target.value as 'ativo' | 'receptivo' | '')}
            className={contatosPortalFiltroFieldClass}
          >
            <option value="">Todos</option>
            <option value="ativo">Ativo</option>
            <option value="receptivo">Receptivo</option>
          </Select>
        </FiltroField>
        <FiltroField label="Sucesso no contato">
          <Select
            value={sucessoContato}
            onChange={e =>
              setSucessoContato(e.target.value as 'com_sucesso' | 'sem_sucesso' | '')
            }
            className={contatosPortalFiltroFieldClass}
          >
            <option value="">Todos</option>
            <option value="com_sucesso">Sim</option>
            <option value="sem_sucesso">Não</option>
          </Select>
        </FiltroField>
        <FiltroField label="Motivo">
          <Select
            value={motivoId}
            onChange={e => setMotivoId(e.target.value)}
            className={contatosPortalFiltroFieldClass}
          >
            <option value="">Todos</option>
            {motivos.map(m => (
              <option key={m.id} value={m.id}>
                {m.name}
              </option>
            ))}
          </Select>
        </FiltroField>
      </ContatosPortalFiltrosSection>

      <ContatosPortalTableSection
        loading={loading}
        error={hasSearched ? error : null}
        hasSearched={hasSearched}
        isEmpty={grupos.length === 0}
        emptyBeforeSearchMessage="Use os filtros e clique em Buscar para carregar o histórico de contatos."
        emptyAfterSearchMessage={emptyMessage}
        currentPage={currentPage}
        totalPages={totalPages}
        total={total}
        pageSize={PAGE_SIZE}
        onPageChange={handlePageChange}
      >
        <HistoricoContatosAgrupadosTable
          key={tableKey}
          grupos={gruposPagina}
          sortKey={sortKey}
          sortOrder={sortOrder}
          onSort={handleColumnSort}
        />
      </ContatosPortalTableSection>
    </div>
  )
}

export default ContatosPortalHistoricoTab
